/**
 * Admin Clients Page — internal user-management console.
 *
 * Single-purpose dashboard exposing every admin action defined in
 * `/app/backend/admin/sentinel_control.py`. Built per the P0
 * Admin-Control-Hardening brief (June 2026):
 *
 *   - list, search, filter users
 *   - grant / revoke indefinite access
 *   - extend trial
 *   - deactivate / reactivate
 *   - suspend (mark-under-review)
 *   - force-logout (bumps token_version)
 *   - view audit history per user
 *   - log out from admin
 *
 * Every action requires a reason. Every response shows success/error.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, LogOut, Search, History, ShieldCheck, ShieldX, Pause, Play, UserX, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "deactivated", label: "Deactivated" },
  { value: "under_review", label: "Under review" },
  { value: "pending_approval", label: "Pending approval" },
  { value: "rejected", label: "Rejected" },
];

// Map UI action name → backend endpoint suffix (under /admin/access/users/:id/)
const ACTION_ENDPOINTS = {
  grant_indefinite_access: "grant-indefinite-access",
  revoke_override:         "revoke-override",
  extend_trial:            "extend-trial",
  deactivate:              "deactivate",
  reactivate:              "restore",
  suspend:                 "mark-under-review",
  clear_review:            "clear-review",
  force_logout:            "force-logout",
};


export default function AdminClientsPage() {
  const { token, logout, user } = useAuth();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [actionFor, setActionFor] = useState(null);   // {user, action, label, requiresDays}
  const [auditFor, setAuditFor] = useState(null);     // {user, audit[]}

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (status && status !== "all") params.set("status", status);
      params.set("limit", "200");
      const { data } = await axios.get(
        `${API}/admin/access/users?${params.toString()}`, auth,
      );
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAction = (u, action, label, requiresDays = false) => {
    setActionFor({ user: u, action, label, requiresDays, reason: "", days: 30 });
  };

  const submitAction = async () => {
    const { user: target, action, reason, days } = actionFor;
    if (!reason || reason.length < 3) {
      setActionFor({ ...actionFor, error: "Reason must be at least 3 characters" });
      return;
    }
    const endpoint = ACTION_ENDPOINTS[action];
    const body = { reason };
    if (action === "extend_trial") body.days = parseInt(days, 10);
    try {
      await axios.post(
        `${API}/admin/access/users/${target.id}/${endpoint}`, body, auth,
      );
      setToast(`${actionFor.label} → ${target.email}: success`);
      setActionFor(null);
      load();
    } catch (e) {
      setActionFor({ ...actionFor, error: e?.response?.data?.detail || "Action failed" });
    }
  };

  const openAudit = async (u) => {
    try {
      const { data } = await axios.get(
        `${API}/admin/access/users/${u.id}`, auth,
      );
      setAuditFor({ user: u, audit: data.audit || [] });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load audit");
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0B1120] text-slate-100"
      data-testid="admin-clients-page"
    >
      {/* Header */}
      <div className="border-b border-slate-800/40 bg-[#020617]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <div className="font-barlow font-bold text-lg">Sentient Vault</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/70 font-manrope">
                Admin Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden sm:block">{user?.email}</span>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-slate-300 hover:text-amber-400"
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <h1 className="font-barlow font-bold text-3xl mb-1">User Management</h1>
        <p className="text-sm text-slate-500 mb-6">
          Total {total} users · platform-owner controls
        </p>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search by email, name, or org…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="admin-search-input"
              className="pl-10 bg-slate-900/50 border-slate-700 h-11"
            />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); }}>
            <SelectTrigger
              className="w-full sm:w-56 bg-slate-900/50 border-slate-700 h-11"
              data-testid="admin-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={load}
            disabled={loading}
            data-testid="admin-search-btn"
            className="h-11 bg-amber-500 hover:bg-amber-400 text-slate-900"
          >
            {loading ? "Loading…" : "Search"}
          </Button>
        </div>

        {/* Error + toast */}
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 mb-4 flex items-center gap-2 text-sm text-red-400" data-testid="admin-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        {toast && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 mb-4 flex items-center gap-2 text-sm text-emerald-400" data-testid="admin-toast">
            <CheckCircle2 className="w-4 h-4" /> {toast}
          </div>
        )}

        {/* User table */}
        <div className="rounded-2xl border border-slate-800/60 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-user-table">
            <thead className="bg-slate-900/50 text-slate-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Org</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Override</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={`${u.id}-${u.email || idx}`} className="border-t border-slate-800/40" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{u.email}</div>
                    {u.full_name && <div className="text-xs text-slate-500">{u.full_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    <code className="text-[11px]">{u.org_id || "—"}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className={
                      "text-[11px] px-2 py-1 rounded-full uppercase tracking-wider " +
                      (u.account_status === "active" ? "bg-emerald-500/15 text-emerald-300" :
                       u.account_status === "suspended" ? "bg-amber-500/15 text-amber-300" :
                       "bg-red-500/15 text-red-300")
                    }>{u.account_status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {u.access_override ? (
                      <span className="text-amber-300 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Indefinite
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex flex-wrap gap-2 justify-end">
                      {!u.access_override ? (
                        <ActionBtn label="Grant Access" testId={`grant-${u.id}`}
                          onClick={() => openAction(u, "grant_indefinite_access", "Grant indefinite access")}/>
                      ) : (
                        <ActionBtn label="Revoke" testId={`revoke-${u.id}`} variant="danger"
                          onClick={() => openAction(u, "revoke_override", "Revoke indefinite access")}/>
                      )}
                      <ActionBtn label="Extend Trial" testId={`extend-${u.id}`}
                        onClick={() => openAction(u, "extend_trial", "Extend trial", true)}/>
                      {u.account_status === "active" ? (
                        <ActionBtn label="Suspend" testId={`suspend-${u.id}`} variant="warn"
                          onClick={() => openAction(u, "suspend", "Suspend / mark under review")}/>
                      ) : u.account_status === "under_review" ? (
                        <ActionBtn label="Clear Review" testId={`clearreview-${u.id}`}
                          onClick={() => openAction(u, "clear_review", "Clear review")}/>
                      ) : null}
                      {u.account_status !== "deactivated" ? (
                        <ActionBtn label="Deactivate" testId={`deactivate-${u.id}`} variant="danger"
                          onClick={() => openAction(u, "deactivate", "Deactivate")}/>
                      ) : (
                        <ActionBtn label="Reactivate" testId={`reactivate-${u.id}`}
                          onClick={() => openAction(u, "reactivate", "Reactivate")}/>
                      )}
                      <ActionBtn label="Force Logout" testId={`forcelogout-${u.id}`}
                        onClick={() => openAction(u, "force_logout", "Force logout")}/>
                      <button
                        className="text-xs text-slate-500 hover:text-slate-300 px-2"
                        onClick={() => openAudit(u)}
                        data-testid={`audit-${u.id}`}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && !loading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No users match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionFor} onOpenChange={(o) => !o && setActionFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle data-testid="admin-action-title">
              {actionFor?.label} — {actionFor?.user?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {actionFor?.requiresDays && (
              <div>
                <Label>Days to extend</Label>
                <Input
                  type="number" min={1} max={365}
                  value={actionFor.days}
                  onChange={(e) => setActionFor({ ...actionFor, days: e.target.value })}
                  data-testid="admin-action-days"
                  className="bg-slate-800/60 border-slate-700"
                />
              </div>
            )}
            <div>
              <Label>Reason (required, audited)</Label>
              <textarea
                rows={3}
                className="w-full mt-1 rounded-md bg-slate-800/60 border border-slate-700 p-2 text-sm"
                value={actionFor?.reason || ""}
                onChange={(e) => setActionFor({ ...actionFor, reason: e.target.value })}
                data-testid="admin-action-reason"
              />
            </div>
            {actionFor?.error && (
              <div className="text-xs text-red-400" data-testid="admin-action-error">
                {actionFor.error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionFor(null)}>Cancel</Button>
            <Button onClick={submitAction} data-testid="admin-action-submit"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit dialog */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl">
          <DialogHeader>
            <DialogTitle>Audit history — {auditFor?.user?.email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="admin-audit-list">
            {!auditFor?.audit?.length && (
              <div className="text-sm text-slate-500">No admin actions recorded for this user yet.</div>
            )}
            {auditFor?.audit?.map((row, i) => (
              <div key={i} className="rounded-lg border border-slate-800/60 bg-slate-800/30 px-3 py-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-amber-300 font-medium">{row.action}</span>
                  <span className="text-slate-500">{row.timestamp}</span>
                </div>
                <div className="text-slate-400">by {row.admin_email || row.admin_id || "system"}</div>
                {row.reason && <div className="text-slate-300 italic mt-1">"{row.reason}"</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ActionBtn({ label, onClick, variant = "default", testId }) {
  const cls =
    variant === "danger" ? "bg-red-500/15 hover:bg-red-500/25 text-red-300 border-red-500/30" :
    variant === "warn"   ? "bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border-amber-500/30" :
                           "bg-slate-700/30 hover:bg-slate-700/50 text-slate-200 border-slate-700/60";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-[11px] px-2.5 py-1 rounded border ${cls} transition-colors`}
    >
      {label}
    </button>
  );
}
